import { createContext, useContext, useEffect, useReducer, useState } from "react";
import { computePrice } from "../data/products";

const CartContext = createContext(null);
const STORAGE_KEY = "he-cart-v1";

const initial = { items: [] };

function reducer(state, action) {
  switch (action.type) {
    case "HYDRATE":
      return action.payload || initial;
    case "ADD": {
      const { product, variant, qty } = action.payload;
      const key = `${product.id}__${variant.length}__${variant.weight}`;
      const existing = state.items.find((i) => i.key === key);
      // Prefer Stripe-derived unit_amount + stripe_price_id from the enriched variant.
      const stripePriceId = variant.stripe_price_id || null;
      const variantPrice =
        typeof variant.unit_amount === "number"
          ? variant.unit_amount
          : computePrice(product, variant) || 0;
      const items = existing
        ? state.items.map((i) =>
            i.key === key ? { ...i, qty: i.qty + qty } : i,
          )
        : [
            ...state.items,
            {
              key,
              productId: product.id,
              stripe_price_id: stripePriceId,
              name: product.name,
              image: product.image,
              image_url: product.image,
              origin: product.origin,
              type: product.type,
              priceLabel: formatHuf(variantPrice),
              unitPrice: variantPrice,
              unit_amount: variantPrice,
              length: variant.length,
              weight: variant.weight,
              qty,
              quantity: qty,
            },
          ];
      return { ...state, items };
    }
    case "UPDATE_QTY": {
      const { key, qty } = action.payload;
      const items = state.items
        .map((i) => (i.key === key ? { ...i, qty: Math.max(0, qty) } : i))
        .filter((i) => i.qty > 0);
      return { ...state, items };
    }
    case "REMOVE":
      return { ...state, items: state.items.filter((i) => i.key !== action.payload) };
    case "CLEAR":
      return initial;
    default:
      return state;
  }
}

function parsePrice(label) {
  if (!label) return 0;
  const digits = String(label).replace(/[^\d]/g, "");
  return parseInt(digits, 10) || 0;
}

export function formatHuf(n) {
  if (!n || isNaN(n)) return "- Ft";
  return new Intl.NumberFormat("hu-HU").format(Math.round(n)) + " Ft";
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initial);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate from localStorage (one-shot on mount)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) dispatch({ type: "HYDRATE", payload: JSON.parse(raw) });
    } catch (e) {
      // ignore
    }
    setHydrated(true);
  }, []);

  // Persist (only after hydration to avoid clobbering existing storage on mount)
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      // ignore
    }
  }, [state, hydrated]);

  const totalQty = state.items.reduce((s, i) => s + i.qty, 0);
  const subtotal = state.items.reduce((s, i) => s + i.unitPrice * i.qty, 0);

  const value = {
    items: state.items,
    totalQty,
    subtotal,
    drawerOpen,
    openDrawer: () => setDrawerOpen(true),
    closeDrawer: () => setDrawerOpen(false),
    addItem: (product, variant, qty = 1) =>
      dispatch({ type: "ADD", payload: { product, variant, qty } }),
    updateQty: (key, qty) => dispatch({ type: "UPDATE_QTY", payload: { key, qty } }),
    removeItem: (key) => dispatch({ type: "REMOVE", payload: key }),
    clear: () => dispatch({ type: "CLEAR" }),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
